import { v4 as uuidv4 } from "uuid";
import type { PocAssignmentDoc } from "../../../../../data-model/pocAssignmentRule";
import type { QueryBuilderValue } from "../../../../../data-model/queryBuilder";
import { DEFAULT_POC_ASSIGNMENT_DOC_ID } from "../../pocAssignment/pocAssignmentUtils";
import type { PocConfigTableRowState } from "../data-model/pocAssignmentModel";

const RULE_SUBTEXT = "Auto-generated from the POC config table";
const POC_ACTION_FIELD = "poc";
/** Condition field names + operators as persisted in the rules doc. */
const FILENAME_FIELD = "filename";
const FILENAME_OPERATOR = "contains_any_of";
const DRUG_COHORT_FIELD = "drug_cohort";
const DRUG_COHORT_OPERATOR = "=";
const ORG_ID_FIELD = "org_id";
const ORG_ID_OPERATOR = "=";

/** Distinct, truthy values preserving first-seen order. */
const distinct = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

/** One cohort group: all of its batches and the available POCs that belong to it. */
interface CohortGroup {
  cohort: string;
  batches: string[];
  pocs: string[];
}

/**
 * Groups rows by cohort. Each group aggregates every batch seen across that
 * cohort's rows and the POCs from rows whose availability is true.
 *
 * @param rows - Current POC config table rows.
 * @returns Groups sorted by cohort for deterministic output.
 */
function groupRowsByCohort(rows: PocConfigTableRowState[]): CohortGroup[] {
  const groups = new Map<string, CohortGroup>();
  rows.forEach((row) => {
    const cohort = row.cohort ?? "";
    const group = groups.get(cohort) ?? { cohort, batches: [], pocs: [] };
    group.batches.push(...(row.batch ?? []).filter(Boolean));
    if (row.availability && row.poc) group.pocs.push(row.poc);
    groups.set(cohort, group);
  });
  return Array.from(groups.values()).sort((a, b) =>
    a.cohort.localeCompare(b.cohort),
  );
}

/**
 * Builds the poc_assignment rules-doc payload from the current table rows.
 *
 * Produces one rule per cohort. Each rule's condition is
 * `batches is any of [all batches for that cohort] AND cohort equals [cohort]`,
 * and its action sets a random POC from the available POCs in that cohort.
 * Existing doc metadata (id, created_at, created_by) is preserved.
 *
 * @param rows - Current POC config table rows.
 * @param existingDoc - The existing poc_assignment doc, for metadata preservation.
 * @param updatedBy - Email to stamp as updated_by.
 * @param orgId - Selected health facility id, stamped on every rule as org_id.
 * @returns The full document payload to write to Firestore.
 */
export function buildPocAssignmentRunPayload(
  rows: PocConfigTableRowState[],
  existingDoc: PocAssignmentDoc | null,
  updatedBy?: string,
  orgId?: string,
): PocAssignmentDoc {
  const rules = groupRowsByCohort(rows).map((group) => {
    const condition: QueryBuilderValue = {
      type: "group",
      id: uuidv4(),
      conjunction: "AND",
      children: [
        {
          type: "condition",
          id: uuidv4(),
          field: FILENAME_FIELD,
          operator: FILENAME_OPERATOR,
          value: distinct(group.batches),
        },
        {
          type: "condition",
          id: uuidv4(),
          field: DRUG_COHORT_FIELD,
          operator: DRUG_COHORT_OPERATOR,
          value: group.cohort,
        },
        {
          type: "condition",
          id: uuidv4(),
          field: ORG_ID_FIELD,
          operator: ORG_ID_OPERATOR,
          value: orgId ?? "",
        },
      ],
    };

    return {
      label: group.cohort || "—",
      subtext: `${RULE_SUBTEXT}: cohort ${group.cohort || "(none)"}`,
      active: true,
      triggers: [{ type: "order_start" }],
      actions: [
        {
          type: "set_random",
          field: POC_ACTION_FIELD,
          value: distinct(group.pocs),
        },
      ],
      rule: condition,
    };
  });

  return {
    id: existingDoc?.id ?? DEFAULT_POC_ASSIGNMENT_DOC_ID,
    rules,
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: new Date().toISOString(),
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}
